import { motion, useInView } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { MapPin } from "lucide-react";

const regions = [
  { id: "niedersachsen", name: "Raum Braunschweig", projects: 128, description: "Unser Kerngebiet. Wir sind im Umkreis von 25 km um Braunschweig im Einsatz.", cities: ["Braunschweig", "Wolfsburg", "Salzgitter", "Peine"], markerPos: { x: "48%", y: "28%" } },
];

// State ID mapping to simplemaps IDs
const stateMapping = {
  nrw: "DENW",
  hessen: "DEHE",
  bayern: "DEBY",
  niedersachsen: "DENI",
  bw: "DEBW",
};

export const MapSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [selectedRegion, setSelectedRegion] = useState(regions[0]);
  const [svgContent, setSvgContent] = useState("");

  useEffect(() => {
    fetch("/germany-map.svg")
      .then(r => r.text())
      .then(text => {
        // Remove fixed width/height, add viewBox for proportional scaling
        let cleaned = text
          .replace(/width="1000"/, '')
          .replace(/height="1000"/, '')
          .replace('<svg ', '<svg style="width:100%;height:100%" ');
        setSvgContent(cleaned);
      })
      .catch(() => {});
  }, []);

  // Highlight selected state in SVG
  useEffect(() => {
    if (!svgContent) return;
    const container = document.getElementById("germany-svg-container");
    if (!container) return;

    // Reset all paths
    const paths = container.querySelectorAll("path");
    paths.forEach(p => {
      p.setAttribute("fill", "hsl(220, 14%, 16%)");
      p.setAttribute("stroke", "hsl(220, 12%, 22%)");
      p.setAttribute("stroke-width", "0.8");
      p.style.cursor = "pointer";
      p.style.transition = "fill 0.3s ease";
    });

    // Highlight selected
    const selectedId = stateMapping[selectedRegion?.id];
    if (selectedId) {
      const selectedPath = container.querySelector(`#${selectedId}`);
      if (selectedPath) {
        selectedPath.setAttribute("fill", "hsl(215, 75%, 50%)");
        selectedPath.setAttribute("stroke", "hsl(215, 75%, 62%)");
        selectedPath.setAttribute("stroke-width", "1.5");
      }
    }

    // Add click handlers
    const reverseMapping = {};
    Object.entries(stateMapping).forEach(([key, val]) => {
      reverseMapping[val] = key;
    });

    paths.forEach(p => {
      const id = p.getAttribute("id");
      if (reverseMapping[id]) {
        p.onclick = () => {
          const region = regions.find(r => r.id === reverseMapping[id]);
          if (region) setSelectedRegion(region);
        };
        p.onmouseenter = () => {
          if (stateMapping[selectedRegion?.id] !== id) {
            p.setAttribute("fill", "hsl(220, 14%, 24%)");
          }
        };
        p.onmouseleave = () => {
          if (stateMapping[selectedRegion?.id] !== id) {
            p.setAttribute("fill", "hsl(220, 14%, 16%)");
          }
        };
      }
    });
  }, [svgContent, selectedRegion]);

  return (
    <section className="section-padding bg-card" ref={ref}>
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="mb-16 max-w-2xl"
        >
          <span className="text-xs font-heading uppercase tracking-widest text-muted-foreground mb-3 block">
            Projektstandorte
          </span>
          <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-foreground mb-4 leading-tight">
            Im Einsatz rund um Braunschweig.
          </h2>
          <p className="text-sm text-muted-foreground font-body leading-relaxed">
            {"Über 128 erfolgreich abgeschlossene Projekte in ganz Deutschland."}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-0">
          {/* Real Germany SVG Map with markers */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="lg:col-span-2 bg-background border border-border p-6 flex items-center justify-center"
          >
            <div className="relative" style={{ maxHeight: '480px', maxWidth: '320px', width: '100%' }}>
              <div
                id="germany-svg-container"
                className="w-full"
                dangerouslySetInnerHTML={{ __html: svgContent }}
              />
              {/* Location markers */}
              {regions.map((region) => (
                <button
                  key={region.id}
                  onClick={() => setSelectedRegion(region)}
                  className={`absolute w-5 h-5 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center transition-all duration-300 ${
                    selectedRegion?.id === region.id ? "scale-125" : "scale-100 hover:scale-110"
                  }`}
                  style={{ left: region.markerPos.x, top: region.markerPos.y }}
                  title={region.name}
                >
                  <MapPin className={`w-4 h-4 drop-shadow-lg ${
                    selectedRegion?.id === region.id ? "text-brand-blue-light" : "text-brand-blue/70"
                  }`} fill={selectedRegion?.id === region.id ? "hsl(215, 75%, 50%)" : "hsl(215, 40%, 30%)"} />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Region List */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-3 flex flex-col"
          >
            {regions.map((region) => (
              <button
                key={region.id}
                onClick={() => setSelectedRegion(region)}
                className={`w-full text-left p-6 md:p-8 border border-border flex flex-col ${
                  selectedRegion?.id === region.id
                    ? "bg-background border-l-2 border-l-brand-blue"
                    : "bg-card hover:bg-background/50"
                }`}
                style={{ transition: 'background-color 0.2s' }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <MapPin className={`w-4 h-4 ${
                      selectedRegion?.id === region.id ? "text-brand-blue-light" : "text-muted-foreground"
                    }`} />
                    <h3 className="font-heading font-semibold text-base text-foreground">
                      {region.name}
                    </h3>
                  </div>
                  <span className="text-sm font-heading font-bold text-brand-blue-light">
                    {region.projects} Projekte
                  </span>
                </div>
                {selectedRegion?.id === region.id && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    className="mt-2"
                  >
                    <p className="text-sm text-muted-foreground font-body leading-relaxed mb-3">
                      {region.description}
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {region.cities.map(city => (
                        <span key={city} className="text-[10px] px-3 py-1 bg-brand-blue-muted text-brand-blue-light font-heading uppercase tracking-wider">
                          {city}
                        </span>
                      ))}
                    </div>
                  </motion.div>
                )}
              </button>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
